import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";
import { Editor, Mark, mergeAttributes, Node, renderNestedMarkdownContent } from "@tiptap/core";
import BaseImage from "@tiptap/extension-image";
import BaseItalic from "@tiptap/extension-italic";
import Link from "@tiptap/extension-link";
import BaseListItem from "@tiptap/extension-list-item";
import BaseSubscript from "@tiptap/extension-subscript";
import BaseSuperscript from "@tiptap/extension-superscript";
import { Markdown } from "@tiptap/markdown";
import StarterKit from "@tiptap/starter-kit";
import { HTML_BLOCK_ATTRIBUTE, protectHtmlBlocks } from "../utils/html_blocks";
import { isHttpUrl, uploadErrorMessage } from "../utils/tiptap_links";

// =============================================================================
// Tiptap Extension Customizations
// These extensions are customized to support Kramdown-compatible markdown output
// =============================================================================

/** Superscript mark with HTML output for Kramdown compatibility */
const Superscript = BaseSuperscript.extend({
	renderMarkdown(node, helpers) {
		return `<sup>${helpers.renderChildren(node)}</sup>`;
	},
});

/** Subscript mark with HTML output for Kramdown compatibility */
const Subscript = BaseSubscript.extend({
	renderMarkdown(node, helpers) {
		return `<sub>${helpers.renderChildren(node)}</sub>`;
	},
});

/** Italic mark using underscore syntax for Kramdown compatibility */
const Italic = BaseItalic.extend({
	renderMarkdown(node, helpers) {
		return `_${helpers.renderChildren(node)}_`;
	},
});

/** ListItem node using asterisk for bullet lists (Kramdown compatibility) */
const ListItem = BaseListItem.extend({
	renderMarkdown(node, helpers, ctx) {
		return renderNestedMarkdownContent(
			node,
			helpers,
			(context) => {
				if (context.parentType === "bulletList") {
					return "* ";
				}
				if (context.parentType === "orderedList") {
					return `${context.index + 1}. `;
				}
				return "* ";
			},
			ctx,
		);
	},
});

/** Image node with markdown parsing and rendering support */
const Image = BaseImage.extend({
	markdownTokenName: "image",
	parseMarkdown(token) {
		return {
			type: "image",
			attrs: {
				src: token.href || "",
				alt: token.text || "",
				title: token.title || null,
			},
		};
	},
	renderMarkdown(node) {
		const { src = "", alt = "", title } = node.attrs || {};
		return title ? `![${alt}](${src} "${title}")` : `![${alt}](${src})`;
	},
});

/**
 * HTML block node - preserves styled divs and SVGs in WYSIWYG (non-editable).
 * Captures divs with class/style attributes (for centering, etc.) and bare SVGs.
 * Content is stored as raw HTML and rendered unchanged in markdown output.
 */
const HTMLBlock = Node.create({
	name: "htmlBlock",
	group: "block",
	atom: true,

	addAttributes() {
		return {
			content: { default: "" },
		};
	},

	parseHTML() {
		return [
			// Chart blocks folded by protectHtmlBlocks before markdown parsing
			{
				tag: `div[${HTML_BLOCK_ATTRIBUTE}]`,
				getAttrs: (el) => ({ content: el.getAttribute(HTML_BLOCK_ATTRIBUTE) }),
			},
			// Styled divs (with class or style) - e.g., centering wrappers
			{
				tag: "div[class]",
				getAttrs: (el) => ({ content: el.outerHTML }),
			},
			{
				tag: "div[style]",
				getAttrs: (el) => ({ content: el.outerHTML }),
			},
			// Bare SVGs (not wrapped in a styled div)
			{
				tag: "svg",
				getAttrs: (el) => ({ content: el.outerHTML }),
			},
		];
	},

	addNodeView() {
		return ({ node }) => {
			const wrapper = document.createElement("div");
			wrapper.className = "tiptap-html-block";
			wrapper.contentEditable = "false";
			wrapper.innerHTML = node.attrs.content;
			return { dom: wrapper };
		};
	},

	renderMarkdown(node) {
		return node.attrs.content;
	},
});

/** Underline mark with HTML output (not native to markdown, uses <u> tags) */
const Underline = Mark.create({
	name: "underline",
	parseHTML() {
		return [{ tag: "u" }, { style: "text-decoration", getAttrs: (style) => (style.includes("underline") ? {} : false) }];
	},
	renderHTML({ HTMLAttributes }) {
		return ["u", mergeAttributes(HTMLAttributes), 0];
	},
	renderMarkdown(node, helpers) {
		return `<u>${helpers.renderChildren(node)}</u>`;
	},
	addCommands() {
		return {
			setUnderline:
				() =>
				({ commands }) =>
					commands.setMark(this.name),
			toggleUnderline:
				() =>
				({ commands }) =>
					commands.toggleMark(this.name),
			unsetUnderline:
				() =>
				({ commands }) =>
					commands.unsetMark(this.name),
		};
	},
	addKeyboardShortcuts() {
		return {
			"Mod-u": () => this.editor.commands.toggleUnderline(),
			"Mod-U": () => this.editor.commands.toggleUnderline(),
		};
	},
});

// =============================================================================
// Toolbar Icons
// =============================================================================

import boldIcon from "../images/icons/basic/bold-solid.svg";
import chevronDownIcon from "../images/icons/basic/chevron-down-solid.svg";
import codeIcon from "../images/icons/basic/code-solid.svg";
import heading1Icon from "../images/icons/basic/heading-1.svg";
import heading2Icon from "../images/icons/basic/heading-2.svg";
import heading3Icon from "../images/icons/basic/heading-3.svg";
import heading4Icon from "../images/icons/basic/heading-4.svg";
import heading5Icon from "../images/icons/basic/heading-5.svg";
import heading6Icon from "../images/icons/basic/heading-6.svg";
import imageIcon from "../images/icons/basic/image-solid.svg";
import italicIcon from "../images/icons/basic/italic-solid.svg";
import linkIcon from "../images/icons/basic/link-solid.svg";
import horizontalRuleIcon from "../images/icons/basic/minus-solid.svg";
import orderedListIcon from "../images/icons/basic/ol-solid.svg";
import quoteIcon from "../images/icons/basic/quote-left-solid.svg";
import undoIcon from "../images/icons/basic/rotate-left-solid.svg";
import redoIcon from "../images/icons/basic/rotate-right-solid.svg";
import strikethroughIcon from "../images/icons/basic/strikethrough-solid.svg";
import subscriptIcon from "../images/icons/basic/subscript-solid.svg";
import superscriptIcon from "../images/icons/basic/superscript-solid.svg";
import unorderedListIcon from "../images/icons/basic/ul-solid.svg";
import underlineIcon from "../images/icons/basic/underline-solid.svg";

// =============================================================================
// Toolbar Configuration
// Defines all available toolbar buttons and their behavior
// - Use "|" for separators
// - Use type: "dropdown" for dropdown menus
// - adminOnly: only shown in admin mode
// - imagesOnly: only shown when images are enabled
// - wysiwygOnly: disabled while the source textarea is being edited
// =============================================================================

const TOOLBAR_BUTTONS = [
	{ icon: undoIcon, command: "undo", label: "undo", wysiwygOnly: true },
	{ icon: redoIcon, command: "redo", label: "redo", wysiwygOnly: true },
	"|",
	{
		type: "dropdown",
		icon: heading2Icon,
		label: "headings",
		active: "heading",
		items: [
			{ icon: heading1Icon, command: "toggleHeading", args: { level: 1 }, label: "heading_1", active: "heading", activeArgs: { level: 1 } },
			{ icon: heading2Icon, command: "toggleHeading", args: { level: 2 }, label: "heading_2", active: "heading", activeArgs: { level: 2 } },
			{ icon: heading3Icon, command: "toggleHeading", args: { level: 3 }, label: "heading_3", active: "heading", activeArgs: { level: 3 } },
			{ icon: heading4Icon, command: "toggleHeading", args: { level: 4 }, label: "heading_4", active: "heading", activeArgs: { level: 4 } },
			{ icon: heading5Icon, command: "toggleHeading", args: { level: 5 }, label: "heading_5", active: "heading", activeArgs: { level: 5 } },
			{ icon: heading6Icon, command: "toggleHeading", args: { level: 6 }, label: "heading_6", active: "heading", activeArgs: { level: 6 } },
		],
	},
	{
		type: "dropdown",
		icon: unorderedListIcon,
		label: "lists",
		items: [
			{ icon: unorderedListIcon, command: "toggleBulletList", label: "bullet_list", active: "bulletList" },
			{ icon: orderedListIcon, command: "toggleOrderedList", label: "ordered_list", active: "orderedList" },
		],
	},
	{ icon: quoteIcon, command: "toggleBlockquote", label: "blockquote", active: "blockquote" },
	{ icon: horizontalRuleIcon, command: "setHorizontalRule", label: "horizontal_rule" },
	"|",
	{ icon: boldIcon, command: "toggleBold", label: "bold", active: "bold" },
	{ icon: italicIcon, command: "toggleItalic", label: "italic", active: "italic" },
	{ icon: strikethroughIcon, command: "toggleStrike", label: "strike", active: "strike" },
	{ icon: codeIcon, command: "toggleCode", label: "code", active: "code", id: "inline-code" },
	{ icon: underlineIcon, command: "toggleUnderline", label: "underline", active: "underline" },
	{ icon: linkIcon, command: "openLinkPrompt", label: "link", active: "link" },
	"|",
	{ icon: superscriptIcon, command: "toggleSuperscript", label: "superscript", active: "superscript" },
	{ icon: subscriptIcon, command: "toggleSubscript", label: "subscript", active: "subscript" },
	"|",
	{ icon: imageIcon, command: "openImagePicker", label: "insert_image", imagesOnly: true },
	{ icon: codeIcon, command: "toggleSourceMode", label: "edit_source", id: "source-toggle", adminOnly: true },
];

/**
 * Markdown format definitions for source mode editing.
 * Maps Tiptap commands to their markdown syntax equivalents.
 */
const SOURCE_MODE_FORMATS = {
	toggleBold: { prefix: "**", suffix: "**" },
	toggleItalic: { prefix: "_", suffix: "_" },
	toggleUnderline: { prefix: "<u>", suffix: "</u>" },
	toggleStrike: { prefix: "~~", suffix: "~~" },
	toggleCode: { prefix: "`", suffix: "`" },
	toggleSuperscript: { prefix: "<sup>", suffix: "</sup>" },
	toggleSubscript: { prefix: "<sub>", suffix: "</sub>" },
	toggleHeading: { prefix: (args) => `${"#".repeat(args?.level || 1)} `, suffix: "", line: true },
	toggleBlockquote: { prefix: "> ", suffix: "", line: true },
	toggleBulletList: { prefix: "* ", suffix: "", line: true },
	toggleOrderedList: { prefix: "1. ", suffix: "", line: true },
	toggleCodeBlock: { insert: "\n```\n\n```\n" },
	setHorizontalRule: { insert: "\n---\n" },
};

/**
 * Regex patterns for content that Tiptap's Markdown parser cannot handle properly.
 * When matched, the editor opens in source mode to prevent content corruption.
 *
 * Matches:
 * - Kramdown IAL attributes: {:class="..."} or {: .class}
 * - Custom tags: MONSTERBOX[...], SPELLBOX[...], YOUTUBE[...]
 * - HTML tables: <table (preserves TailwindCSS classes)
 * - Images with Kramdown attributes: ![alt](url){:...}
 *
 * Does NOT match (supported by editor):
 * - Plain markdown images: ![alt](url)
 * - HTML tags: <sup>, <sub>, <u>
 * - Inline SVG: <svg (handled by HTMLBlock extension)
 * - Styled divs: <div class="..."> or <div style="..."> (handled by HTMLBlock extension)
 */
const COMPLEX_SYNTAX_PATTERN = /\{:|MONSTERBOX\[|SPELLBOX\[|YOUTUBE\[|<table|!\[.+?\]\(.+?\)\s*\{/i;

// =============================================================================
// Controller
// =============================================================================

export default class extends Controller {
	static targets = ["input", "editor", "toolbar", "blobIds", "source"];
	static values = {
		admin: { type: Boolean, default: false },
		images: { type: Boolean, default: false },
		publicCdn: { type: String, default: "" },
		labels: Object,
	};

	// ---------------------------------------------------------------------------
	// Lifecycle
	// ---------------------------------------------------------------------------

	connect() {
		// If images value wasn't explicitly set, inherit from admin value
		if (!this.hasImagesValue) {
			this.imagesValue = this.adminValue;
		}

		this.sourceMode = false;
		this.openDropdown = null;
		this.toolbarButtons = [];
		this.wysiwygOnlyButtons = [];

		this.initEditor();
		this.initToolbar();
		this.initFormSync();

		// Auto-resize source textarea on input
		this.boundAutoResizeSource = () => this.autoResizeSource();
		this.sourceTarget.addEventListener("input", this.boundAutoResizeSource);

		// Close dropdowns when clicking outside
		this.boundCloseDropdown = (e) => {
			if (this.openDropdown && !this.openDropdown.contains(e.target)) {
				this.closeDropdown();
			}
		};
		document.addEventListener("click", this.boundCloseDropdown);

		// Sync content before sortable drag starts (so content survives DOM moves)
		this.boundSyncBeforeSort = () => this.syncContent();
		document.addEventListener("sortable:start", this.boundSyncBeforeSort);
	}

	disconnect() {
		clearTimeout(this.errorTimer);
		document.removeEventListener("sortable:start", this.boundSyncBeforeSort);
		this.sourceTarget.removeEventListener("input", this.boundAutoResizeSource);
		document.removeEventListener("click", this.boundCloseDropdown);
		this.form?.removeEventListener("submit", this.boundSyncContent);
		this.editor?.destroy();
		this.editor = null;
	}

	// ---------------------------------------------------------------------------
	// Editor Setup
	// ---------------------------------------------------------------------------

	/** Creates the Tiptap editor instance with all configured extensions */
	initEditor() {
		const content = this.inputTarget.value || "";
		const hasComplexSyntax = this.adminValue && COMPLEX_SYNTAX_PATTERN.test(content);

		this.editor = new Editor({
			element: this.editorTarget,
			extensions: this.buildExtensions(),
			editorProps: {
				attributes: {
					class: "tiptap-editor-content odo-markdown focus:outline-none min-h-[300px] p-4",
				},
				handleDrop: this.handleDrop.bind(this),
				handlePaste: this.handlePaste.bind(this),
			},
			// Only edits made here clear the stored text; setMarkdown never emits.
			onUpdate: () => {
				this.text = null;
			},
			onCreate: () => {
				// Skip WYSIWYG parsing for complex content to preserve tables, SVG, etc.
				if (hasComplexSyntax) {
					this.sourceTarget.value = content;
					this.editorTarget.classList.add("hidden");
					this.sourceTarget.classList.remove("hidden");
					this.sourceMode = true;
					this.autoResizeSource();
					this.sourceToggleButton?.classList.add("is-active");
				} else if (content) {
					this.setMarkdown(content);
				}
				// Re-serializing rewrites Markdown that renders the same, so the stored
				// text is kept until a visual edit forces serialization.
				this.text = content;
			},
		});
	}

	/** Builds the list of Tiptap extensions based on current configuration */
	buildExtensions() {
		const extensions = [
			// StarterKit.configure({ underline: false, italic: false, listItem: false, heading: { levels: [1, 2, 3, 4, 5, 6] } }),
			StarterKit.configure({ underline: false, italic: false, listItem: false, link: false }),
			Markdown.configure({ html: this.adminValue }),
			Underline,
			Italic,
			ListItem,
			Link.configure({
				openOnClick: false,
				isAllowedUri: (url) => isHttpUrl(url),
				shouldAutoLink: (url) => isHttpUrl(url),
			}),
			Superscript,
			Subscript,
		];

		// Admin content may hold images even where uploads are off.
		if (this.imagesValue || this.adminValue) {
			extensions.push(Image.configure({ allowBase64: false, inline: false }));
		}

		// Add HTML block support for admin users (preserves styled divs and SVGs in WYSIWYG)
		if (this.adminValue) {
			extensions.push(HTMLBlock);
		}

		return extensions;
	}

	// ---------------------------------------------------------------------------
	// Toolbar
	// ---------------------------------------------------------------------------

	/** Builds and renders the toolbar based on configuration and current mode */
	initToolbar() {
		this.toolbarTarget.innerHTML = "";
		this.toolbarButtons = [];

		for (const btn of this.getVisibleToolbarButtons()) {
			if (btn === "|") {
				this.toolbarTarget.appendChild(this.createSeparator());
			} else if (btn.type === "dropdown") {
				this.toolbarTarget.appendChild(this.createDropdown(btn));
			} else {
				const button = this.createButton(btn);
				this.toolbarTarget.appendChild(button);
				if (btn.active) {
					this.toolbarButtons.push({ element: button, config: btn });
				}
			}
		}

		this.linkPopover = this.createLinkPopover();
		this.toolbarTarget.insertAdjacentElement("afterend", this.linkPopover);
		this.errorLine = document.createElement("p");
		this.errorLine.className = "tiptap-error hidden";
		this.errorLine.setAttribute("role", "alert");
		this.linkPopover.insertAdjacentElement("afterend", this.errorLine);

		// Update button states when editor selection changes
		this.editor.on("selectionUpdate", () => this.updateToolbarState());
		this.editor.on("transaction", () => this.updateToolbarState());
	}

	/** Filters toolbar buttons based on admin/images settings */
	getVisibleToolbarButtons() {
		return TOOLBAR_BUTTONS.filter((btn) => {
			if (btn === "|") return true;
			if (btn.adminOnly && !this.adminValue) return false;
			if (btn.imagesOnly && !this.imagesValue) return false;
			return true;
		});
	}

	/** Creates a visual separator element for the toolbar */
	createSeparator() {
		const span = document.createElement("span");
		span.className = "tiptap-toolbar-separator";
		return span;
	}

	/** Creates a dropdown button with a menu of sub-options */
	createDropdown(config) {
		const wrapper = document.createElement("div");
		wrapper.className = "tiptap-toolbar-dropdown";

		const trigger = document.createElement("button");
		trigger.type = "button";
		trigger.className = "tiptap-toolbar-button tiptap-dropdown-trigger";
		const triggerLabel = this.labelsValue[config.label];
		trigger.title = triggerLabel;
		trigger.innerHTML = `<img src="${config.icon}" alt="${triggerLabel}" class="w-4 h-4" /><img src="${chevronDownIcon}" alt="" class="w-2 h-2 ml-0.5 opacity-60" />`;

		const menu = document.createElement("div");
		menu.className = "tiptap-dropdown-menu hidden";

		for (const item of config.items) {
			const btn = document.createElement("button");
			btn.type = "button";
			btn.className = "tiptap-dropdown-item";
			const itemLabel = this.labelsValue[item.label];
			btn.title = itemLabel;
			btn.innerHTML = `<img src="${item.icon}" alt="${itemLabel}" class="w-4 h-4" /><span class="ml-2">${itemLabel}</span>`;
			btn.addEventListener("mousedown", (e) => {
				e.preventDefault();
				this.executeCommand(item);
				this.closeDropdown();
			});
			if (item.active) {
				this.toolbarButtons.push({ element: btn, config: item });
			}
			menu.appendChild(btn);
		}

		trigger.addEventListener("mousedown", (e) => {
			e.preventDefault();
			e.stopPropagation();
			if (this.openDropdown === wrapper) {
				this.closeDropdown();
			} else {
				this.closeDropdown();
				menu.classList.remove("hidden");
				trigger.classList.add("is-open");
				this.openDropdown = wrapper;
			}
		});

		if (config.active) {
			this.toolbarButtons.push({ element: trigger, config, isDropdownTrigger: true });
		}

		wrapper.appendChild(trigger);
		wrapper.appendChild(menu);
		return wrapper;
	}

	/** Closes any open dropdown menu */
	closeDropdown() {
		if (this.openDropdown) {
			this.openDropdown.querySelector(".tiptap-dropdown-menu")?.classList.add("hidden");
			this.openDropdown.querySelector(".tiptap-dropdown-trigger")?.classList.remove("is-open");
			this.openDropdown = null;
		}
	}

	/** Creates a single toolbar button */
	createButton(config) {
		const button = document.createElement("button");
		button.type = "button";
		button.className = "tiptap-toolbar-button";
		const label = this.labelsValue[config.label];
		button.title = label;
		button.innerHTML = `<img src="${config.icon}" alt="${label}" class="w-4 h-4" />`;
		if (config.id) button.id = config.id;
		if (config.wysiwygOnly) this.wysiwygOnlyButtons.push(button);
		// Store reference to source toggle button for this instance
		if (config.id === "source-toggle") {
			this.sourceToggleButton = button;
		}
		// Use mousedown to prevent focus loss from editor
		button.addEventListener("mousedown", (e) => {
			e.preventDefault();
			this.executeCommand(config);
		});
		return button;
	}

	/** Updates toolbar button active states based on current editor selection */
	updateToolbarState() {
		for (const { element, config, isDropdownTrigger } of this.toolbarButtons) {
			let isActive;
			if (isDropdownTrigger && config.items) {
				// Dropdown is active if any of its items are active
				isActive = config.items.some((item) => (item.activeArgs ? this.editor.isActive(item.active, item.activeArgs) : this.editor.isActive(item.active)));
			} else {
				isActive = config.activeArgs ? this.editor.isActive(config.active, config.activeArgs) : this.editor.isActive(config.active);
			}
			element.classList.toggle("is-active", isActive);
		}
	}

	// ---------------------------------------------------------------------------
	// Command Execution
	// ---------------------------------------------------------------------------

	/** Executes a toolbar command, routing to the appropriate handler */
	executeCommand(config) {
		// Custom commands handled by controller methods
		switch (config.command) {
			case "openImagePicker":
				return this.openImagePicker();
			case "toggleSourceMode":
				return this.toggleSourceMode();
			case "openLinkPrompt":
				return this.openLinkPrompt();
		}

		// In source mode, manipulate the textarea directly
		if (this.sourceMode) {
			return this.executeSourceCommand(config);
		}

		// Standard Tiptap editor commands
		const chain = this.editor.chain().focus();
		if (config.args) {
			chain[config.command](config.args).run();
		} else {
			chain[config.command]().run();
		}
	}

	/** Executes a formatting command in source mode by inserting markdown syntax */
	executeSourceCommand(config) {
		const format = SOURCE_MODE_FORMATS[config.command];
		if (!format) return;

		const textarea = this.sourceTarget;
		const { selectionStart: start, selectionEnd: end, value: text } = textarea;
		const selected = text.substring(start, end);

		let newText;
		let cursorPos;

		if (format.insert) {
			// Insert fixed text (e.g., horizontal rule, code block)
			newText = text.substring(0, start) + format.insert + text.substring(end);
			cursorPos = start + format.insert.length;
		} else if (format.line) {
			// Line-based formatting (e.g., headings, lists)
			const prefix = typeof format.prefix === "function" ? format.prefix(config.args) : format.prefix;
			const lineStart = text.lastIndexOf("\n", start - 1) + 1;
			newText = text.substring(0, lineStart) + prefix + text.substring(lineStart);
			cursorPos = end + prefix.length;
		} else {
			// Inline formatting (e.g., bold, italic)
			const prefix = typeof format.prefix === "function" ? format.prefix(config.args) : format.prefix;
			newText = text.substring(0, start) + prefix + selected + format.suffix + text.substring(end);
			cursorPos = start + prefix.length + selected.length + format.suffix.length;
		}

		textarea.value = newText;
		textarea.selectionStart = textarea.selectionEnd = cursorPos;
		textarea.focus();
		this.autoResizeSource();
	}

	// ---------------------------------------------------------------------------
	// Link Handling
	// ---------------------------------------------------------------------------

	/** Builds the inline panel that replaces window.prompt for links */
	createLinkPopover() {
		const panel = document.createElement("div");
		panel.className = "tiptap-link-popover hidden";
		panel.innerHTML = `
			<label class="tiptap-link-popover__field">
				<span class="odo-form__label">${this.labelsValue.link_url}</span>
				<input type="url" class="odo-form__input-text tiptap-link-popover__input" placeholder="https://" spellcheck="false" autocomplete="off">
			</label>
			<button type="button" class="odo-button__primary" data-link-action="apply">${this.labelsValue.link_apply}</button>
			<button type="button" class="odo-button__alt" data-link-action="remove">${this.labelsValue.link_remove}</button>
			<button type="button" class="odo-button__alt" data-link-action="cancel">${this.labelsValue.link_cancel}</button>
			<span class="tiptap-link-popover__error hidden"></span>`;

		this.linkInput = panel.querySelector("input");
		this.linkError = panel.querySelector(".tiptap-link-popover__error");
		this.linkInput.addEventListener("keydown", (e) => {
			if (e.key === "Enter") {
				e.preventDefault();
				this.applyLink();
			} else if (e.key === "Escape") {
				e.preventDefault();
				this.closeLinkPopover();
			}
		});
		panel.addEventListener("click", (e) => {
			const action = e.target.closest("[data-link-action]")?.dataset.linkAction;
			if (action === "apply") this.applyLink();
			if (action === "remove") this.removeLink();
			if (action === "cancel") this.closeLinkPopover();
		});
		return panel;
	}

	/** Opens the link panel with the current link (WYSIWYG) or a fresh URL (source mode) */
	openLinkPrompt() {
		this.linkInput.value = this.sourceMode ? "https://" : this.editor.getAttributes("link").href || "";
		this.linkError.classList.add("hidden");
		this.linkPopover.classList.remove("hidden");
		this.linkInput.focus();
		this.linkInput.select();
	}

	closeLinkPopover() {
		this.linkPopover.classList.add("hidden");
		if (this.sourceMode) {
			this.sourceTarget.focus();
		} else {
			this.editor.commands.focus();
		}
	}

	/** Applies the typed URL: sets the link mark, or inserts markdown in source mode */
	applyLink() {
		const url = this.linkInput.value.trim();
		if (url === "" && !this.sourceMode) return this.removeLink();

		if (!isHttpUrl(url)) {
			this.linkError.textContent = this.labelsValue.link_invalid;
			this.linkError.classList.remove("hidden");
			this.linkInput.focus();
			return;
		}

		if (this.sourceMode) {
			this.insertSourceLink(url);
		} else {
			this.editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
		}
		this.closeLinkPopover();
	}

	removeLink() {
		if (!this.sourceMode) this.editor.chain().focus().extendMarkRange("link").unsetLink().run();
		this.closeLinkPopover();
	}

	/** Wraps the source selection in a markdown link */
	insertSourceLink(url) {
		const textarea = this.sourceTarget;
		const { selectionStart: start, selectionEnd: end, value: text } = textarea;
		const linkText = text.substring(start, end) || "link";
		const markdown = `[${linkText}](${url})`;
		textarea.value = text.substring(0, start) + markdown + text.substring(end);
		textarea.selectionStart = textarea.selectionEnd = start + markdown.length;
		this.autoResizeSource();
	}

	/** Shows a message under the toolbar instead of a blocking alert */
	showError(message) {
		clearTimeout(this.errorTimer);
		this.errorLine.textContent = message;
		this.errorLine.classList.remove("hidden");
		this.errorTimer = setTimeout(() => this.errorLine.classList.add("hidden"), 8000);
	}

	// ---------------------------------------------------------------------------
	// Source Mode
	// ---------------------------------------------------------------------------

	/** Toggles between WYSIWYG and source (markdown) editing modes */
	toggleSourceMode() {
		this.linkPopover.classList.add("hidden");
		if (this.sourceMode) {
			// Switching from source to WYSIWYG
			this.text = this.sourceTarget.value;
			this.setMarkdown(this.text);
			this.sourceTarget.classList.add("hidden");
			this.editorTarget.classList.remove("hidden");
			this.sourceToggleButton?.classList.remove("is-active");
		} else {
			// Switching from WYSIWYG to source
			this.sourceTarget.value = this.text ?? this.currentMarkdown();
			this.editorTarget.classList.add("hidden");
			this.sourceTarget.classList.remove("hidden");
			this.sourceToggleButton?.classList.add("is-active");
			this.autoResizeSource();
			this.sourceTarget.focus();
		}

		this.sourceMode = !this.sourceMode;
		for (const button of this.wysiwygOnlyButtons) button.disabled = this.sourceMode;
	}

	/** Loads markdown into the WYSIWYG editor, keeping chart blocks intact */
	setMarkdown(markdown) {
		this.editor.commands.setContent(protectHtmlBlocks(markdown), { contentType: "markdown", emitUpdate: false });
	}

	/** Adjusts source textarea height to fit content */
	autoResizeSource() {
		this.sourceTarget.style.height = "auto";
		this.sourceTarget.style.height = `${this.sourceTarget.scrollHeight}px`;
	}

	// ---------------------------------------------------------------------------
	// Image Upload
	// ---------------------------------------------------------------------------

	/** Opens a file picker for image upload */
	openImagePicker() {
		const input = document.createElement("input");
		input.type = "file";
		input.accept = "image/*";
		input.multiple = true;
		input.onchange = (e) => {
			Array.from(e.target.files).forEach((file) => {
				this.uploadImage(file);
			});
		};
		input.click();
	}

	/** Uploads an image via Active Storage direct upload */
	uploadImage(file) {
		const sanitizedFile = new File([file], this.sanitizeFilename(file.name), { type: file.type });
		const upload = new DirectUpload(sanitizedFile, "/rails/active_storage/direct_uploads");

		upload.create((error, blob) => {
			if (error) {
				console.error("Upload failed:", error);
				this.showError(uploadErrorMessage(error, this.labelsValue));
				return;
			}

			// Use Cloudflare R2 public URL if configured, otherwise fall back to full Rails redirect URL
			const url = this.publicCdnValue
				? `https://${this.publicCdnValue}/${blob.key}`
				: `${window.location.origin}/rails/active_storage/blobs/redirect/${blob.signed_id}/${blob.filename}`;

			if (this.sourceMode) {
				// Insert markdown syntax into textarea
				const textarea = this.sourceTarget;
				const { selectionStart: start, value: text } = textarea;
				const markdown = `![](${url})`;
				textarea.value = text.substring(0, start) + markdown + text.substring(start);
				textarea.selectionStart = textarea.selectionEnd = start + markdown.length;
				this.autoResizeSource();
			} else {
				this.editor.chain().focus().setImage({ src: url }).run();
			}

			this.trackBlobId(blob.signed_id);
		});
	}

	/** Removes accents and special characters from filename */
	sanitizeFilename(name) {
		return name
			.normalize("NFD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/[^a-zA-Z0-9._-]/g, "-")
			.replace(/-+/g, "-")
			.replace(/^-|-$/g, "");
	}

	/** Tracks uploaded blob IDs for form submission */
	trackBlobId(signedId) {
		if (!this.hasBlobIdsTarget) return;
		const ids = this.blobIdsTarget.value ? this.blobIdsTarget.value.split(",") : [];
		if (!ids.includes(signedId)) {
			ids.push(signedId);
			this.blobIdsTarget.value = ids.join(",");
		}
	}

	// ---------------------------------------------------------------------------
	// Drag & Drop / Paste
	// ---------------------------------------------------------------------------

	/** Handles image drop events */
	handleDrop(_view, event, _slice, moved) {
		if (moved || !this.imagesValue) return false;
		const files = this.getImageFiles(event.dataTransfer?.files);
		if (files.length === 0) return false;
		event.preventDefault();
		files.forEach((file) => {
			this.uploadImage(file);
		});
		return true;
	}

	/** Handles image paste events */
	handlePaste(_view, event) {
		if (!this.imagesValue) return false;
		const items = event.clipboardData?.items;
		if (!items) return false;

		for (const item of items) {
			if (item.type.startsWith("image/")) {
				event.preventDefault();
				const file = item.getAsFile();
				if (file) this.uploadImage(file);
				return true;
			}
		}
		return false;
	}

	/** Filters a FileList to only include image files */
	getImageFiles(fileList) {
		if (!fileList) return [];
		return Array.from(fileList).filter((f) => f.type.startsWith("image/"));
	}

	// ---------------------------------------------------------------------------
	// Form Sync
	// ---------------------------------------------------------------------------

	/** Sets up form submission handler to sync editor content */
	initFormSync() {
		this.boundSyncContent = () => this.syncContent();
		this.form = this.element.closest("form");
		this.form?.addEventListener("submit", this.boundSyncContent);
	}

	/** Syncs editor content to the hidden input field before form submission */
	syncContent() {
		if (this.sourceMode) {
			this.inputTarget.value = this.sourceTarget.value;
		} else {
			this.inputTarget.value = this.text ?? this.currentMarkdown();
		}
	}

	currentMarkdown() {
		return this.editor.isEmpty ? "" : this.editor.getMarkdown();
	}
}
